import { INFO_WIDTH, TRACK_EVENTS } from "core/consts";
import { getAuctionStatusString } from "core/model/auctions";
import { isPrintMode } from "core/model/utils/urls";
import { Auction } from "core/types";
import { EditPatientButtonSchema } from "core/validationSchemas/tracking";
import RSButton from "ds_legacy/components/RSButton";
import { Title } from "ds_legacy/materials/typography";
import { openPrintPage, usePreventPrintMenu } from "dsl/atoms/PrintButton";
import { useCareseekerNavigationHandlers } from "dsl/hooks";
import { PencilIcon, PrinterIcon } from "lucide-react";
import { useTracking } from "react-tracking";
import { useTranslations } from "translations";
import { PatientInfoContext } from "../PatientProfileDetailsShared";
import { CareNeedsCard } from "../PatientProfileDetailsShared/CareNeedsCard";
import { InformationCard } from "../PatientProfileDetailsShared/InformationCard";
import { MedicalCard } from "../PatientProfileDetailsShared/MedicalCard";
import { MedicalSuppliesSearchCard } from "../PatientProfileDetailsShared/MedicalSuppliesSearchCard";
import { PatientProfileInfoCard } from "../PatientProfileDetailsShared/shared";
import { TransportInfoCard } from "../PatientProfileDetailsShared/TransportInfoCard";

const EditButton = ({ auction }: { auction: Auction }) => {
  const translations = useTranslations();
  const { trackEvent } = useTracking<EditPatientButtonSchema>();
  const appNavigation = useCareseekerNavigationHandlers();
  return (
    <RSButton
      aria-label={translations.actions.edit}
      color="grey"
      id="go_to_assessing"
      LeftIcon={PencilIcon}
      loading="na"
      variant="text"
      onClick={() => {
        trackEvent({
          auction_status: getAuctionStatusString(auction?.status),
          name: TRACK_EVENTS.EDIT_PATIENT_BUTTON_CLICKED,
        });
        appNavigation.patient.goToAssessing({
          auctionId: auction.id,
          patientId: auction.patient.id,
        });
      }}
    >
      {translations.actions.edit}
    </RSButton>
  );
};

const PrintButtonV2 = () => {
  const isPrint = isPrintMode();
  const translations = useTranslations();
  const { trackEvent } = useTracking();

  usePreventPrintMenu();

  if (isPrint) return null;

  return (
    <RSButton
      aria-label={translations.actions.print}
      color="grey"
      id="print"
      LeftIcon={PrinterIcon}
      loading="na"
      variant="text"
      onClick={() => {
        trackEvent({ name: TRACK_EVENTS.PRINT_BUTTON });
        openPrintPage(window);
      }}
    >
      {translations.actions.print}
    </RSButton>
  );
};

export function PatientProfileDetailsCareseeker({
  auction,
}: {
  auction: Auction;
}) {
  const translations = useTranslations();

  return (
    <PatientInfoContext.Provider value={{ auction }}>
      <div
        className="flex flex-1 flex-col border"
        data-testid="patient-information"
        style={{ maxWidth: INFO_WIDTH }}
      >
        <PatientProfileInfoCard noDivider>
          <div className="flex justify-between">
            <Title>
              {translations.patient.patientInformation.stepperTitle}
            </Title>
            <div className="flex items-center gap-2">
              <EditButton auction={auction} />
              <PrintButtonV2 />
            </div>
          </div>
        </PatientProfileInfoCard>
        <InformationCard auction={auction} />
        <MedicalSuppliesSearchCard auction={auction} />
        <MedicalCard auction={auction} forClinic />
        <CareNeedsCard auction={auction} />
        <TransportInfoCard auction={auction} />
      </div>
    </PatientInfoContext.Provider>
  );
}
